import { QuickApiServiceBase, QuickConsoleLogger } from 'hooks/services';
import { appApiProvider } from './axiosApiProvider';

interface SessionTokenStorage {
    token: string;
    expiresAt: string;
    applicationId: string;
    email: string;
    storedAt: string;
}

class OTPService extends QuickApiServiceBase {
    constructor() {
        super(new QuickConsoleLogger('OTPService'), appApiProvider, 'otp', 'OTP');
    }

    // API calls

    public async verifyEmail(id: string, email: string): Promise<any> {
        return this.postOrThrow({ applicationId: id, email }, { url: `/verify-email` });
    }

    public async request(id: string): Promise<any> {
        return this.postOrThrow({ applicationId: id }, { url: `/request` });
    }

    public async validate(id: string, code: string): Promise<any> {
        try {
            const result = await this.postOrThrow({ applicationId: id, code }, { url: `/validate` });

            console.log('🔐 OTP validation response:', result);

            // Type assertion - the External Backend will return the session token structure
            const response = result as any;

            // Handle session token response from External Backend
            if (response && response.sessionToken) {
                const sessionData: SessionTokenStorage = {
                    token: response.sessionToken,
                    expiresAt: response.expiresAt || '',
                    applicationId: id,
                    email: response.sessionInfo?.email || '',
                    storedAt: new Date().toISOString(),
                };

                // Store session token securely
                this.storeSessionToken(id, sessionData);

                console.log('✅ Session token stored successfully');
            }

            return result;
        } catch (error) {
            console.error('❌ OTP validation error:', error);
            throw error;
        }
    }

    // Session token management

    public getSessionToken(applicationId: string): SessionTokenStorage | null {
        try {
            const storageKey = `otp_session_${applicationId}`;
            const stored = localStorage.getItem(storageKey);

            if (!stored) {
                return null;
            }

            const sessionData: SessionTokenStorage = JSON.parse(stored);

            // Check if session expired
            if (new Date(sessionData.expiresAt) <= new Date()) {
                console.log('⏰ Session token expired, removing...');
                this.clearSessionToken(applicationId);
                return null;
            }

            return sessionData;
        } catch (error) {
            console.error('❌ Failed to retrieve session token:', error);
            return null;
        }
    }

    public clearSessionToken(applicationId: string): void {
        try {
            const storageKey = `otp_session_${applicationId}`;
            localStorage.removeItem(storageKey);

            // Check if this was the last session
            const hasOtherSessions = this.hasAnyActiveSessions();
            if (!hasOtherSessions) {
                localStorage.removeItem('has_active_session');
            }

            console.log(`🗑️ Session token cleared for app: ${applicationId}`);
        } catch (error) {
            console.error('❌ Failed to clear session token:', error);
        }
    }

    public hasAnyActiveSessions(): boolean {
        try {
            const keys = Object.keys(localStorage);
            const sessionKeys = keys.filter((key) => key.startsWith('otp_session_'));

            for (const key of sessionKeys) {
                const stored = localStorage.getItem(key);
                if (stored) {
                    try {
                        const sessionData: SessionTokenStorage = JSON.parse(stored);
                        if (new Date(sessionData.expiresAt) > new Date()) {
                            return true;
                        }
                    } catch {
                        // Invalid session data, continue
                    }
                }
            }

            return false;
        } catch (error) {
            console.error('❌ Failed to check active sessions:', error);
            return false;
        }
    }

    private storeSessionToken(applicationId: string, sessionData: SessionTokenStorage): void {
        try {
            // Store session token in localStorage with structured data
            const storageKey = `otp_session_${applicationId}`;
            localStorage.setItem(storageKey, JSON.stringify(sessionData));

            // Global session flag for compatibility (temporary)
            localStorage.setItem('has_active_session', 'true');

            console.log(`🔒 Session token stored for app: ${applicationId}`);
        } catch (error) {
            console.error('❌ Failed to store session token:', error);
        }
    }
}

export const otpService = new OTPService();
